import React, { useState, useEffect, useRef } from "react";

export default function OrigamiCommunicationModal({
  open,
  text,
  audioUrl,
  onClose,
  autoClose = true,
}) {
  const [displayedText, setDisplayedText] = useState("");
  const [waveformBars, setWaveformBars] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);
  const speechRef = useRef(null);
  const hasSpokenRef = useRef(false);

  // Animation waveform (barres qui bougent de manière plus organique)
  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setWaveformBars(Array.from({ length: 60 }, (_, i) => {
        // Effet de vague sinusoïdale
        const wave = Math.sin((Date.now() / 200) + (i / 5)) * 0.5 + 0.5;
        const random = Math.random() * 0.3;
        return Math.min(wave + random, 1);
      }));
    }, 50);

    return () => clearInterval(interval);
  }, [open]);

  // Animation texte progressif (lettre par lettre)
  useEffect(() => {
    if (!open) {
      setDisplayedText("");
      return;
    }

    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 40);

    return () => clearInterval(interval);
  }, [open, text]);

  // Text-to-Speech (voix IA) - FIX BOUCLE
  useEffect(() => {
    if (!open || !text) return;
    if (hasSpokenRef.current) return; // Ne parler qu'une seule fois

    hasSpokenRef.current = true; // Marquer comme déjà parlé

    // Si audioUrl est fourni, on l'utilise
    if (audioUrl && audioRef.current) {
      audioRef.current.play().catch(err => {
        console.warn("Audio autoplay bloqué:", err);
      });
      setIsSpeaking(true);

      audioRef.current.onended = () => {
        setIsSpeaking(false);
        if (autoClose) {
          setTimeout(() => onClose?.(), 1000);
        }
      };
    }
    // Sinon, utiliser Web Speech API (synthèse vocale)
    else if ('speechSynthesis' in window) {
      const speak = () => {
        const utterance = new SpeechSynthesisUtterance(text);

        // Configuration de la voix
        utterance.lang = 'fr-FR';
        utterance.rate = 0.9;
        utterance.pitch = 0.8;
        utterance.volume = 1;

        const voices = speechSynthesis.getVoices();
        console.log('Voix disponibles:', voices.map(v => v.name + ' (' + v.lang + ')'));

        const frVoice = voices.find(v => v.lang.startsWith('fr'));
        if (frVoice) {
          utterance.voice = frVoice;
          console.log('Voix sélectionnée:', frVoice.name);
        }

        utterance.onstart = () => {
          console.log('Speech started');
          setIsSpeaking(true);
        };

        utterance.onend = () => {
          console.log('Speech ended');
          setIsSpeaking(false);
          if (autoClose) {
            setTimeout(() => onClose?.(), 1000);
          }
        };

        utterance.onerror = (e) => {
          console.error('Speech error:', e);
          setIsSpeaking(false);
        };

        speechRef.current = utterance;
        speechSynthesis.speak(utterance);
      };

      const voices = speechSynthesis.getVoices();

      if (voices.length > 0) {
        setTimeout(speak, 500);
      } else {
        const onVoicesChanged = () => {
          speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
          setTimeout(speak, 500);
        };
        speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
      }
    } else {
      console.warn('Web Speech API non disponible');
    }

    // Cleanup amélioré
    return () => {
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    };
  }, [open, text, audioUrl]); // Retirer autoClose et onClose des dépendances

  // Reset quand fermeture
  useEffect(() => {
    if (!open) {
      setDisplayedText("");
      setWaveformBars([]);
      setIsSpeaking(false);
      hasSpokenRef.current = false; // Reset le flag

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    }
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Fond noir avec effet glitch subtil */}
      <div
        className="absolute inset-0 bg-black"
        style={{
          animation: "glitch-bg 0.3s infinite",
        }}
      />

      {/* Overlay glitch léger */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "repeating-linear-gradient(0deg, rgba(0, 255, 255, 0.03) 0px, transparent 2px, transparent 4px, rgba(0, 255, 255, 0.03) 4px)",
          animation: "glitch-lines 8s linear infinite",
        }}
      />

      {/* Contenu */}
      <div className="relative z-10 max-w-3xl w-full px-8">
        {/* Bouton fermer (en haut à droite) */}
        <button
          onClick={handleClose}
          className="absolute top-0 right-8 text-cyan-400 hover:text-cyan-300 transition text-3xl"
          aria-label="Fermer"
        >
          ×
        </button>

        {/* Avatar "silhouette mystérieuse" */}
        <div className="flex justify-center mb-6">
          <div className="relative w-24 h-24">
            {/* Cercle extérieur pulsant */}
            <div
              className="absolute inset-0 rounded-full border-2 border-cyan-400/40 animate-ping"
              style={{ animationDuration: '2s' }}
            />

            {/* Cercle intérieur */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 border-2 border-cyan-400 backdrop-blur-sm flex items-center justify-center">
              {/* Silhouette humanoïde abstraite */}
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="opacity-70">
                {/* Tête */}
                <circle cx="12" cy="7" r="3" fill="currentColor" className="text-cyan-300" />
                {/* Corps */}
                <path
                  d="M12 11C8.5 11 6 13 6 16V20H18V16C18 13 15.5 11 12 11Z"
                  fill="currentColor"
                  className="text-cyan-300"
                />
                {/* Effet de "données" */}
                <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="0.5" className="text-cyan-400 opacity-30" />
                <circle cx="12" cy="7" r="5" stroke="currentColor" strokeWidth="0.3" className="text-cyan-400 opacity-20" />
              </svg>
            </div>

            {/* Points de scan animés */}
            <div className="absolute inset-0 flex items-center justify-center">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full bg-cyan-400"
                  style={{
                    animation: `orbit ${2 + i}s linear infinite`,
                    animationDelay: `${i * 0.7}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Label ORIGAMI */}
        <div className="text-center mb-4">
          <p className="text-xs uppercase tracking-[0.3em] text-cyan-400/60 font-mono">
            ORIGAMI IA
          </p>
        </div>

        {/* Waveform visualization (améliorée) */}
        <div className="flex items-end justify-center gap-0.5 h-24 mb-8 px-4">
          {waveformBars.map((height, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-cyan-500 to-cyan-300 transition-all duration-75 rounded-t-full"
              style={{
                height: `${height * 100}%`,
                opacity: isSpeaking ? 0.6 + height * 0.4 : 0.2,
                boxShadow: isSpeaking ? `0 0 ${height * 8}px rgba(34, 211, 238, ${height * 0.6})` : 'none',
                minHeight: '2px',
              }}
            />
          ))}
        </div>

        {/* Sous-titres */}
        <div className="text-center px-4">
          <p
            className="text-lg md:text-xl text-cyan-100 font-mono tracking-wide leading-relaxed"
            style={{
              textShadow: "0 0 8px rgba(34, 211, 238, 0.4)",
            }}
          >
            {displayedText}
            {displayedText.length < text.length && (
              <span className="inline-block w-2 h-5 ml-1 bg-cyan-400 animate-pulse" />
            )}
          </p>
        </div>

        {/* Indicateur vocal */}
        {isSpeaking && (
          <div className="flex justify-center mt-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30">
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-3 bg-cyan-400 rounded-full animate-pulse"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <span className="text-xs text-cyan-300 font-mono">En cours de lecture...</span>
            </div>
          </div>
        )}

        {/* Bouton continuer (si pas autoClose) */}
        {!autoClose && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleClose}
              className="px-6 py-3 rounded-lg bg-cyan-600/20 border border-cyan-400 text-cyan-300 text-sm font-mono hover:bg-cyan-600/40 transition uppercase tracking-wider"
            >
              Continuer
            </button>
          </div>
        )}

        {/* Audio (caché, si fourni) */}
        {audioUrl && (
          <audio ref={audioRef} src={audioUrl} preload="auto" />
        )}
      </div>

      {/* CSS animations */}
      <style jsx>{`
        @keyframes glitch-bg {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.98; }
        }
        
        @keyframes glitch-lines {
          0% { transform: translateY(0); }
          100% { transform: translateY(4px); }
        }

        @keyframes orbit {
          0% {
            transform: rotate(0deg) translateX(40px) rotate(0deg);
          }
          100% {
            transform: rotate(360deg) translateX(40px) rotate(-360deg);
          }
        }
      `}</style>
    </div>
  );
}